import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, friendlyError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Mail, MessageCircle, Send, Sparkles, CheckCircle2 } from "lucide-react";

const TOPICS = [
  { code: "Spolupráce", emoji: "🤝", helper: "Mám firmu nebo nápad na spolupráci." },
  { code: "Srazy a akce", emoji: "🎉", helper: "Mám nápad na sraz nebo akci, který bych chtěl/a uskutečnit s partou." },
  { code: "Cestování", emoji: "✈️", helper: "Mám nápad na společné cestování nebo zájezd." },
  { code: "Návrh na vylepšení", emoji: "💡", helper: "Mám nápad jak ConnectYou ještě vylepšit." },
  { code: "Problém", emoji: "🛠️", helper: "Něco mi tu nefunguje nebo se mi něco nelíbí." },
  { code: "Jiné", emoji: "💌", helper: "Něco jiného." },
];

export default function ContactDialog({ open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    topic: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Refresh form fields when user logs in / dialog opens
  React.useEffect(() => {
    if (open && !done) {
      setForm((f) => ({
        ...f,
        name: f.name || user?.name || "",
        email: f.email || user?.email || "",
      }));
    }
    if (!open) {
      // reset done state on close so next open works fresh
      setTimeout(() => setDone(false), 200);
    }
  }, [open, user, done]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (form.message.trim().length < 10) {
      setError("Napiš prosím alespoň pár vět (min. 10 znaků).");
      return;
    }
    if (!form.topic) {
      setError("Vyber prosím téma.");
      return;
    }
    setSending(true);
    try {
      await api.post("/requests", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        topic: form.topic,
        message: form.message.trim(),
      });
      setDone(true);
      setForm({ name: user?.name || "", email: user?.email || "", topic: "", message: "" });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="contact-dialog" className="max-w-lg rounded-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 grid place-items-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="font-heading text-2xl font-extrabold text-left">Napiš nám 💌</DialogTitle>
              <p className="text-xs text-slate-500 font-medium">Odpovídáme obvykle do 24 hodin.</p>
            </div>
          </div>
        </DialogHeader>

        {done ? (
          <div data-testid="contact-success" className="text-center py-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="font-heading text-xl font-extrabold text-slate-900 mt-4">Hotovo! 🌅</p>
            <p className="text-slate-600 font-medium mt-1">Přečteme si tvoji zprávu a co nejdřív se ti ozveme na e-mail.</p>
            <button
              data-testid="contact-close-btn"
              onClick={onClose}
              className="mt-5 px-6 py-2.5 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm"
            >
              Zavřít
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
            {/* Topic */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">O čem píšeš?</label>
              <div className="grid grid-cols-2 gap-2" data-testid="contact-topic-grid">
                {TOPICS.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    data-testid={`contact-topic-${t.code}`}
                    onClick={() => { set("topic", t.code); if (!form.message) set("message", t.helper); }}
                    className={[
                      "p-3 rounded-xl border-2 text-left transition-all active:scale-[0.98]",
                      form.topic === t.code ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-300",
                    ].join(" ")}
                  >
                    <span className="text-xl mr-2">{t.emoji}</span>
                    <span className="font-bold text-sm text-slate-800">{t.code}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Tvoje jméno</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  maxLength={80}
                  className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">E-mail</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">Zpráva</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Napiš nám, co máš na srdci…"
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium resize-none"
              />
              <div className="text-xs text-slate-400 font-bold mt-1 text-right">{form.message.length}/2000</div>
            </div>

            {error && (
              <div data-testid="contact-error" className="rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
            )}

            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)] flex items-center justify-center gap-2"
            >
              {sending ? "Odesílám…" : <><Send className="w-4 h-4" /> Odeslat</>}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
